import React from 'react';
import { Box, Typography, Alert, Stack } from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Selection } from '@syncfusion/ej2-react-grids';
import { useAppSelector } from '../../../../../app/store';
import type { ProcedureType } from '../../../../../services/EncounterService/types/encounterTypes';
import { ServiceEditDialog } from '../../dialogs';
import { useServiceActions, useServiceEditDialog } from './hooks';
import { 
  ActionsTemplate, 
  primaryServiceTemplate, 
  startTimeTemplate, 
  endTimeTemplate, 
  durationTemplate 
} from './templates';

const ServicesGrid: React.FC = () => {
  // Get procedures from Redux store
  const encounter = useAppSelector((state) => state.encounter.currentEncounter);
  const procedures = encounter?.procedures || [];

  // Check if encounter is cancelled
  const isEncounterCancelled = encounter?.status === 'Cancelled';

  // Sort procedures by priority for consistent display
  const sortedProcedures = [...procedures].sort((a, b) => (a.priority || 0) - (b.priority || 0));

  // Hooks
  const { handleMoveUp, handleMoveDown } = useServiceActions();
  const { editDialogOpen, selectedProcedure, handleRowSelection, handleClose } = useServiceEditDialog(
    sortedProcedures,
    isEncounterCancelled
  );

  // Actions template for move up/down buttons
  const actionsTemplate = (props: any) => {
    const procedure = sortedProcedures.find((proc) => proc.id === props.id);
    if (!procedure) return null;

    const currentIndex = sortedProcedures.findIndex((p) => p.id === procedure.id);
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === sortedProcedures.length - 1;

    return (
      <ActionsTemplate
        procedureId={procedure.id!}
        isFirst={isFirst}
        isLast={isLast}
        isDisabled={isEncounterCancelled}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
      />
    );
  };

  // Primary service template wrapper that finds the procedure
  const primaryTemplate = (props: any) => {
    const procedure = sortedProcedures.find((proc) => proc.id === props.id);
    return primaryServiceTemplate(procedure || ({} as ProcedureType));
  };

  // Time templates that find the procedure from sorted list
  const startTemplate = (props: any) => {
    const procedure = sortedProcedures.find((proc) => proc.id === props.id);
    return startTimeTemplate(procedure || ({} as ProcedureType));
  };

  const endTemplate = (props: any) => {
    const procedure = sortedProcedures.find((proc) => proc.id === props.id);
    return endTimeTemplate(procedure || ({} as ProcedureType));
  };

  const durTemplate = (props: any) => {
    const procedure = sortedProcedures.find((proc) => proc.id === props.id);
    return durationTemplate(procedure || ({} as ProcedureType));
  };

  // Transform data for grid display
  const gridData =
    sortedProcedures?.map((proc) => ({
      id: proc.id,
      priority: proc.priority,
      serviceCode: proc.serviceCode || 'N/A',
      description: proc.description || proc.serviceDescription || 'N/A',
      modifier: proc.modifier || 'N/A',
      room: proc.room || 'N/A',
    })) || [];

  const gridSettings = {
    allowPaging: true,
    pageSettings: { pageSize: 10 },
    selectionSettings: { type: 'Single' as const, mode: 'Row' as const },
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Services ({procedures.length})
        </Typography>
        {procedures.length > 0 && (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
            Use arrows to reorder priority • Click on a row to edit service
          </Typography>
        )}
      </Stack>

      {procedures.length === 0 ? (
        <Alert severity="info">No services found for this encounter.</Alert>
      ) : (
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <GridComponent
            dataSource={gridData}
            {...gridSettings}
            height="auto"
            rowSelected={handleRowSelection}
            style={{
              border: 'none',
            }}
          >
            <ColumnsDirective>
              <ColumnDirective
                headerText="Actions"
                width="90"
                textAlign="Center"
                template={actionsTemplate}
                allowSorting={false}
              />
              <ColumnDirective field="priority" headerText="Priority" width="80" textAlign="Center" />
              <ColumnDirective
                headerText="Type"
                width="100"
                textAlign="Center"
                template={primaryTemplate}
              />
              <ColumnDirective
                field="serviceCode"
                headerText="Service Code"
                width="120"
                textAlign="Center"
              />
              <ColumnDirective field="description" headerText="Description" width="250" />
              <ColumnDirective field="modifier" headerText="Modifier" width="100" textAlign="Center" />
              <ColumnDirective
                headerText="Start Time"
                width="150"
                textAlign="Center"
                template={startTemplate}
              />
              <ColumnDirective
                headerText="End Time"
                width="150"
                textAlign="Center"
                template={endTemplate}
              />
              <ColumnDirective
                headerText="Duration"
                width="100"
                textAlign="Center"
                template={durTemplate}
              />
              <ColumnDirective field="room" headerText="Room" width="80" textAlign="Center" />
            </ColumnsDirective>
            <Inject services={[Page, Selection]} />
          </GridComponent>
        </Box>
      )}

      {/* Edit Dialog */}
      <ServiceEditDialog procedure={selectedProcedure} open={editDialogOpen} onClose={handleClose} />
    </Box>
  );
};

export default ServicesGrid;
